import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APIEventSystem } from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { gsap } from "gsap";
import { Sprite, Texture } from "pixi.js";

import { AbstractReel, IReelData } from "../../../abstracts/elements/AbstractReel";
import { AbstractScatterAnticipation } from "../../../abstracts/elements/AbstractScatterAnticipation";
import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import { EventsManager, ReelsEvents } from "../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { FakeScatterData, ReelPaddingData } from "../../../abstracts/types/interfaces";
import { getRandomElement } from "../../../abstracts/utils/JSUtils";
import { playSound, stopSound } from "../../../abstracts/utils/SoundUtils";
import { DDD } from "../../DDD";
import { DDDSoundNames, DDDSpineNames } from "../../assetsLoader/DDDAssetsNames";
import {
    allFeaturesReelSet,
    lowMedReelSet,
    normalConfig,
    normalReelSet,
    nudgeReelSet,
    reelsConfig,
    xGodReelSet,
    xGodSymbolGenerateRepeat,
} from "../../config/DDDConfig";
import { DDDSpecialSymbolNames, DDDSymbolNames } from "../../types/DDDEnums";
import { DDDScatterSymbol } from "./symbols/DDDScatterSymbol";
import { DDDSymbol } from "./symbols/DDDSymbol";
import { getSymbolFromPool, returnSymbolToPool } from "./symbols/DDDSymbolsPool";
import { DDDXNudgeSymbol } from "./symbols/DDDXNudgeSymbol";

export const reelGridOffsetX: number[] = [-12, -2, -4, -5, -2];

let xGodSymbolGenerateCounter: number = 0;

export class DDDReel extends AbstractReel {
    private animatedStepData: { y: number };
    private masterSpinTimeline: gsap.core.Timeline | undefined;
    private nudgeSteps: number = 0;
    private paddingConfig: ReelPaddingData;
    private reelSetIndex: number = 0;
    private isSpinning: boolean = false;
    private stopIndex: number;
    private stopping: boolean = false;
    private stopTimeline: gsap.core.Timeline | undefined;
    private stopTriggerTl: gsap.core.Timeline | undefined;
    private triggerStop: boolean = false;
    private grid: Sprite;
    private xGodAnticipation: AbstractScatterAnticipation;
    private scatterSymbol: DDDScatterSymbol | undefined;

    protected customStepDuration: number = 0;

    public xNudgeSymbol: DDDXNudgeSymbol | undefined;
    public readonly events: APIEventSystem | undefined;

    constructor(reelId: number, symbols: string[], triggerEvents: boolean = true) {
        super(reelId, symbols);

        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
        }

        this.customStepDuration = normalConfig.stepDuration!;
    }

    protected override generateInitialSymbols(symbols: string[]): void {
        super.generateInitialSymbols(symbols);

        this.customStepDuration = normalConfig.stepDuration!;
        this.currentSymbols = [];
        this.stopSymbols = [];
        this.animatedStepData = { y: 0 };

        this.paddingConfig = {
            visualPaddingBefore: 1,
            paddingBefore: 0,
            visibleSymbols: 3,
            paddingAfter: 0,
            visualPaddingAfter: 1,
        };

        const totalInitCount =
            this.paddingConfig.paddingBefore +
            this.paddingConfig.visibleSymbols +
            this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.reelId}`);
        }

        symbols = [getRandomElement(lowMedReelSet)].concat(symbols);
        symbols = symbols.concat([getRandomElement(lowMedReelSet)]);

        for (let i: number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(
                symbols[i],
                i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore
            );
            if (
                DDD.model.isRestore &&
                symbol instanceof DDDScatterSymbol &&
                DDD.model.data.nextMode === AbstractGameMode.SUPER_FREESPIN &&
                DDD.model.data.mode !== AbstractGameMode.SUPER_FREESPIN
            ) {
                symbol.getMainSpine().setSkinByName(`sfs`);
            }

            this.currentSymbols.push(symbol);
            this.stopSymbols.push(symbol);

            if (symbol instanceof DDDXNudgeSymbol && i > 0 && i < 4) {
                const xNudgeSymbol = symbol;

                if (
                    i > 1 &&
                    (symbols[i - 1] === DDDSymbolNames.XN_1 ||
                        symbols[i - 1] === DDDSymbolNames.XN_2)
                ) {
                    xNudgeSymbol.visible = false;
                } else {
                    xNudgeSymbol.visible = true;
                    this.xNudgeSymbol = xNudgeSymbol;
                }
            }
        }

        this.updatePosition();

        for (let i: number = 0; i < this.currentSymbols.length; i++) {
            this.currentSymbols[i].positionInArray = i;
        }
    }

    protected override get reelSet(): string[] {
        if (this.reelId === 1 || this.reelId === 2 || this.reelId === 3) {
            if (
                DDD.model.data.mode === AbstractGameMode.NORMAL ||
                DDD.model.data.mode === AbstractGameMode.RESPIN
            ) {
                return allFeaturesReelSet;
            }

            return nudgeReelSet;
        }

        if (
            (this.reelId === 0 || this.reelId === 4) &&
            DDD.model.data.mode === AbstractGameMode.NORMAL
        ) {
            return xGodReelSet;
        }

        return normalReelSet;
    }

    protected override initializeOtherGraphics() {
        super.initializeOtherGraphics();

        this.xGodAnticipation = new AbstractScatterAnticipation(
            this,
            normalConfig.nearWinDuration!,
            DDDSpineNames.XGOD_ANTICIPATION,
            `anticipation_Xgod`
        );
        this.xGodAnticipation.name = `xGodAnticipation${this.reelId}`;
        this.xGodAnticipation.x = 70;
        this.xGodAnticipation.y = 280;

        this.scatterAnticipation = new AbstractScatterAnticipation(
            this,
            normalConfig.nearWinDuration!,
            DDDSpineNames.ANTICIPATION,
            `anticipation`
        );
        this.scatterAnticipation.name = `scatterNearWin${this.reelId}`;
        this.scatterAnticipation.x = 70;
        this.scatterAnticipation.y = 280;

        this.grid = new Sprite(Texture.from(`DDD/grid${this.reelId}`));
        this.grid.name = `gridBar`;
        this.grid.x = reelGridOffsetX[this.reelId];
        this.grid.y = 153;
        this.addChild(this.grid);
    }

    private getNextSymbol(position: number): AbstractSymbol {
        if (this.stopping && this.stopNormalBetAnimation.length > 0) {
            const symbol = this.stopSymbols[this.stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }

        let randomElement = getRandomElement(this.reelSet);
        if (
            randomElement === DDDSymbolNames.XGOD_3 &&
            xGodSymbolGenerateCounter >= xGodSymbolGenerateRepeat
        ) {
            xGodSymbolGenerateCounter = 0;
        } else if (
            randomElement === DDDSymbolNames.XGOD_3 &&
            xGodSymbolGenerateCounter < xGodSymbolGenerateRepeat
        ) {
            randomElement = getRandomElement(lowMedReelSet);
        }
        xGodSymbolGenerateCounter++;

        return this.createNewSymbol(randomElement, position);
    }

    private createNewSymbol(name: string, position: number): AbstractSymbol {
        const symbol = getSymbolFromPool(name);

        this.symbolsContainer.addChild(symbol);
        symbol.reelPosition = position;

        return symbol;
    }

    private clearSymbols(symbols: AbstractSymbol[]): void {
        let symbol;

        // eslint-disable-next-line no-cond-assign
        while ((symbol = symbols.pop())) {
            returnSymbolToPool(symbol);
        }
    }

    public override startNormalBetAnimation(): gsap.core.Timeline {
        if (this.isLocked) {
            return gsap.timeline();
        }

        this.customStepDuration = normalConfig.stepDuration!;

        this.scatterSymbol = undefined;

        const tl = gsap.timeline({
            onStart: () => {
                this.stopSymbols = [];
                this.nudgeSteps = 0;
            },
            onComplete: () => {
                this.startSpin();
            },
        });

        const tween = this.step();
        tween.vars.ease = gsap.parseEase(`back.in(1.5)`);
        tween.duration(normalConfig.stepDuration! * 8);

        tl.add(tween);
        tl.add(() => {
            this.addChild(this.grid);
        });

        return tl;
    }

    public override stopNormalBetAnimation(
        data: IReelData,
        onReelComplete: Function,
        onLandStart: Function = () => {}
    ): gsap.core.Timeline {
        if (this.isLocked) {
            return gsap.timeline({
                smoothChildTiming: true,
                autoRemoveChildren: true,
                onComplete: () => {
                    onReelComplete(this.reelId);
                },
            });
        }

        this.clearSymbols(this.stopSymbols);

        this.customStepDuration = normalConfig.stepDuration!;
        this.stopSymbols = [];
        data.stopSymbolNames = [getRandomElement(lowMedReelSet)].concat(data.stopSymbolNames!);
        data.stopSymbolNames = data.stopSymbolNames!.concat([getRandomElement(lowMedReelSet)]);

        for (let i: number = 0; i < data.stopSymbolNames.length; i++) {
            const symbol = this.createNewSymbol(
                data.stopSymbolNames[i],
                i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore
            );
            symbol.y =
                normalConfig.stepSize! *
                -(
                    this.paddingConfig.visualPaddingBefore +
                    this.paddingConfig.paddingBefore +
                    this.paddingConfig.visibleSymbols
                );

            if (symbol instanceof DDDScatterSymbol && i > 0 && i < 4) {
                this.scatterSymbol = symbol;
                if (
                    DDD.model.gameData.nextMode === AbstractGameMode.SUPER_FREESPIN &&
                    this.reelId === reelsConfig.reelsNumber - 1
                ) {
                    this.scatterSymbol.getMainSpine().setSkinByName(`sfs`);

                    if (DDD.model.gameData.mode === AbstractGameMode.FREESPIN) {
                        this.scatterSymbol.getUpgradeAnimation();
                    }
                }
            }

            if (symbol instanceof DDDXNudgeSymbol && i > 0 && i < 4) {
                const xNudgeSymbol = symbol;

                if (
                    i > 1 &&
                    (data.stopSymbolNames[i - 1] === DDDSymbolNames.XN_1 ||
                        data.stopSymbolNames[i - 1] === DDDSymbolNames.XN_2)
                ) {
                    xNudgeSymbol.visible = false;
                } else {
                    xNudgeSymbol.visible = true;
                    this.xNudgeSymbol = xNudgeSymbol;
                }
            }

            if (symbol.name.includes(`B`) && i > 0 && i < 4) {
                if (
                    i > 1 &&
                    (data.stopSymbolNames[i - 1] === DDDSymbolNames.XGOD_1 ||
                        data.stopSymbolNames[i - 1] === DDDSymbolNames.XGOD_2)
                ) {
                    symbol.visible = false;
                } else {
                    symbol.visible = true;
                }
            }

            this.stopSymbols.push(symbol);
        }

        this.stopTimeline = gsap.timeline({
            paused: true,
            onStart: () => {
                this.stopIndex = data.stopSymbolNames!.length - 1;
                this.stopping = true;
            },
            onComplete: () => {
                this.stopTimeline = undefined;
                this.stopping = false;
            },
        });

        const numberOfNormalSteps =
            this.paddingConfig.visualPaddingBefore +
            this.paddingConfig.paddingBefore +
            this.paddingConfig.visibleSymbols +
            this.paddingConfig.paddingAfter +
            this.paddingConfig.visualPaddingAfter;

        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            const tween = this.step();
            this.stopTimeline.add(tween);
        }

        this.stopTimeline.add(() => {
            this.bounce(data.hasNearWin!, onReelComplete, onLandStart);
        });

        this.stopTriggerTl = gsap.timeline({
            onStart: () => {
                this.isSpinning = false;
                this.triggerStop = true;
            },
        });

        this.stopTriggerTl.add(
            gsap.to(this, {
                duration: this.stopTimeline.totalDuration(),
            })
        );

        const teaseBlockedSymbolsLabel: string = `teaseBlockedSymbols`;

        this.stopTriggerTl.addLabel(teaseBlockedSymbolsLabel);

        return this.stopTriggerTl;
    }

    protected bounce(
        hasNearWin: boolean,
        onReelComplete: Function = () => {},
        onLandStart: Function = () => {}
    ): gsap.core.Timeline {
        const bounceFactor = 0.1;

        const hasFastSpin = SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN);

        const tl = gsap.timeline();

        const updateFakeScatterPosition = () => {
            if (this.scatterSymbol !== undefined) {
                const scatterSymbolGlobalPosition = this.scatterSymbol.getGlobalPosition();
                const scatterData: FakeScatterData = {
                    reelIndex: this.reelId,
                    symbolGlobalPosition: {
                        x: scatterSymbolGlobalPosition.x,
                        y: scatterSymbolGlobalPosition.y,
                    },
                };
                EventsManager.trigger(ReelsEvents.UPDATE_FAKE_SCATTER_POSITION, scatterData);
            }
        };
        const overshoot = gsap.to(this.animatedStepData, {
            duration: hasFastSpin ? 0.05 : 0.1,
            y: hasFastSpin
                ? (normalConfig.stepSize! * bounceFactor) / 2
                : normalConfig.stepSize! * bounceFactor,
            onUpdate: () => {
                this.updatePosition();

                updateFakeScatterPosition();
            },
            onStart: () => {
                if (this.scatterSymbol) {
                    EventsManager.trigger(ReelsEvents.ADD_FAKE_SCATTER, {
                        reelIndex: this.reelId,
                    } as FakeScatterData);
                    updateFakeScatterPosition();
                }

                onLandStart(this.reelId);
            },
            onComplete: () => {
                if (this.reelId === reelsConfig.reelsNumber - 1) {
                    stopSound({ name: DDDSoundNames.SCATTER_ANTICIPATION });
                } else {
                    if (hasNearWin) {
                        playSound({
                            name: DDDSoundNames.SCATTER_ANTICIPATION_REEL_STOP,
                        });
                    }
                }
            },
        });

        const back = gsap.to(this.animatedStepData, {
            duration: hasFastSpin ? 0.1 : 0.2,
            y: 0,
            ease: `back.out(2)`,
            onStart: () => {
                if (this.reelId === 4) {
                    stopSound({ name: DDDSoundNames.REELS_SPIN });
                }
            },
            onUpdate: () => {
                this.updatePosition();

                updateFakeScatterPosition();
            },
            onComplete: () => {
                updateFakeScatterPosition();

                onReelComplete(this.reelId);
            },
        });

        tl.add(overshoot);
        tl.add(back);

        const landingTimeline = gsap.timeline({
            onStart: () => {
                this.addChild(this.symbolsContainer);
            },
        });

        this.currentSymbols.forEach((symbol, symbolIndex) => {
            if (symbolIndex >= 1 && symbolIndex <= 4) {
                if (
                    symbol.name === DDDSpecialSymbolNames.WILD ||
                    symbol.name === DDDSpecialSymbolNames.SCATTER ||
                    symbol.name === DDDSpecialSymbolNames.XN_1 ||
                    symbol.name === DDDSpecialSymbolNames.XN_2 ||
                    symbol.name === DDDSpecialSymbolNames.XN_3 ||
                    symbol.name === DDDSpecialSymbolNames.XGOD_1 ||
                    symbol.name === DDDSpecialSymbolNames.XGOD_2 ||
                    symbol.name === DDDSpecialSymbolNames.XGOD_3
                ) {
                    landingTimeline.add(symbol.getLandingAnimation(), 0);
                }
            }
        });

        tl.add(landingTimeline, 0);

        return tl;
    }

    private step(): gsap.core.Tween {
        return gsap.to(this.animatedStepData, {
            duration: this.customStepDuration,
            y: normalConfig.stepSize!,
            ease: `none`,

            onStart: () => {
                this.addSymbol();
            },
            onUpdate: () => {
                this.updatePosition();
            },
            onComplete: () => this.onAnimationComplete(),
        });
    }

    private spin(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                if (this.isSpinning) {
                    this.masterSpinTimeline?.add(this.spin());
                }
            },
        });
        tl.add(this.step());
        return tl;
    }

    private startSpin() {
        this.isSpinning = true;
        this.masterSpinTimeline = gsap.timeline({});
        this.masterSpinTimeline.add(this.spin());

        this.currentSymbols.forEach((symbol) => {
            (symbol as DDDSymbol).isSpinning = true;
        });
    }

    private onAnimationComplete() {
        for (let i: number = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.reelPosition += 1;
            symbol.positionInArray = i;
        }

        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this.stopTimeline?.resume();
        }

        this.updateIndex();
        this.removeSymbolFromReel();
        this.updatePosition();
    }

    private updateIndex() {
        const valueChange = -1;
        if (this.stopping) {
            this.setStopIndex(this.stopIndex + valueChange);
        } else {
            this.setReelIndex(this.reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value: number) {
        if (value >= this.stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this.stopSymbols.length - 1;
        }
        this.stopIndex = value;
    }

    private setReelIndex(value: number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this.reelSetIndex = value;
    }

    private addSymbol() {
        let newSymbols = [];

        const newSymbolPosition = this.currentSymbols[0].reelPosition - 1;
        const newSymbol = this.getNextSymbol(newSymbolPosition);

        newSymbols.push(newSymbol);

        this.currentSymbols = this.currentSymbols.concat(newSymbols);
        this.currentSymbols.sort((a: any, b: any) => {
            return a.reelPosition < b.reelPosition ? -1 : 1;
        });

        if (this.isSpinning) {
            this.currentSymbols.forEach((symbol) => {
                (symbol as DDDSymbol).isSpinning = true;
            });
        }
    }

    private removeSymbolFromReel() {
        for (let i: number = this.currentSymbols.length - 1; i >= 0; i--) {
            const symbol = this.currentSymbols[i];
            if (
                symbol.reelPosition >=
                this.paddingConfig.visibleSymbols +
                    this.paddingConfig.paddingAfter +
                    this.paddingConfig.visualPaddingAfter
            ) {
                //Remove from bottom
                const removedSymbol = this.currentSymbols.splice(i, 1)[0];
                returnSymbolToPool(removedSymbol);
            }
            if (
                symbol.reelPosition <
                -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore
            ) {
                //Remove from top
                const removedSymbol = this.currentSymbols.splice(i, 1)[0];
                returnSymbolToPool(removedSymbol);
            }
        }
    }

    private updatePosition() {
        for (let i: number = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            const basePos = normalConfig.stepSize! * symbol.reelPosition;

            symbol.y = basePos + this.animatedStepData.y;
        }
    }

    public override getWinningSymbols(winningArr: boolean[]): DDDSymbol[] {
        if (this.xNudgeSymbol !== undefined && this.xNudgeSymbol.visible) {
            return [this.xNudgeSymbol as DDDSymbol];
        }

        const visibleSymbols = this.getAllVisibleStopSymbols();

        const winningSymbols: DDDSymbol[] = [];

        visibleSymbols.forEach((symbol, index) => {
            if (winningArr[index]) {
                winningSymbols.push(symbol as DDDSymbol);
            }
        });

        return winningSymbols;
    }

    public getNonWinningSymbols(winningArr: boolean[]): DDDSymbol[] {
        const visibleSymbols = this.getAllVisibleStopSymbols();

        const nonWinningSymbols: DDDSymbol[] = [];

        visibleSymbols.forEach((symbol, index) => {
            if (!winningArr[index]) {
                nonWinningSymbols.push(symbol as DDDSymbol);
            }
        });

        return nonWinningSymbols;
    }

    public getAllVisibleStopSymbols(): AbstractSymbol[] {
        const paddingBefore =
            this.paddingConfig.visualPaddingBefore +
            this.paddingConfig.paddingBefore +
            this.nudgeSteps;

        return this.stopSymbols.slice(
            paddingBefore,
            paddingBefore + this.paddingConfig.visibleSymbols
        );
    }

    public override getShowNearWinAnimation(
        isXGod: boolean = false
    ): gsap.core.Tween | gsap.core.Timeline {
        const showNearWinAnimation = isXGod
            ? this.xGodAnticipation.getShowAnimation()
            : super.getShowNearWinAnimation();

        const speedObj: { value: number } = { value: normalConfig.stepDuration! };

        const speedUpSpin = gsap.to(speedObj, {
            value: normalConfig.stepDuration! * 0.7,
            duration: normalConfig.nearWinDuration! / 2,
            onUpdate: () => {
                this.customStepDuration = speedObj.value;
            },
        });

        const tl = gsap.timeline();
        tl.add(showNearWinAnimation, 0);
        tl.add(speedUpSpin, 0);

        return tl;
    }

    public override getHideNearWinAnimation(
        isXGod: boolean = false
    ): gsap.core.Tween | gsap.core.Timeline {
        if (isXGod) {
            return this.xGodAnticipation.getHideAnimation();
        }

        return super.getHideNearWinAnimation();
    }

    public clearCurrentSymbols(): void {
        this.currentSymbols.forEach((symbol) => {
            returnSymbolToPool(symbol);
        });

        super.clearCurrentSymbols();
    }

    public getXNudgeAnimation(shouldIdleAnimate: boolean = false): gsap.core.Timeline {
        if (this.xNudgeSymbol === undefined) {
            return gsap.timeline();
        }

        let playIdleAnimationFunction: Function | null = null;

        if (shouldIdleAnimate) {
            playIdleAnimationFunction = () => {
                this.xNudgeSymbol!.getIdleAnimation();
            };
        }
        const timeline = gsap.timeline({
            smoothChildTiming: true,
            onStart: () => {
                this.symbolsContainer.addChild(this.xNudgeSymbol!);
            },
            onComplete: () => {
                if (playIdleAnimationFunction !== null) {
                    playIdleAnimationFunction();
                }

                this.stopSymbols.forEach((symbol, symbolIndex) => {
                    if (symbolIndex > 0 && symbolIndex < 4 && symbol !== this.xNudgeSymbol) {
                        symbol.visible = false;
                    }
                });
            },
        });

        timeline.add(
            this.xNudgeSymbol.getNudgeAnimation(this.isLocked, DDD.model.gameData.win > 0)
        );

        timeline.call(() => {
            this.isLocked = this.xNudgeSymbol!.isSticky;
        });

        return timeline;
    }

    public override resetOnStartSpin() {
        super.resetOnStartSpin();

        if (!this.isLocked) {
            this.currentSymbols.forEach((symbol) => {
                if (this.xNudgeSymbol !== undefined) {
                    if (symbol === this.xNudgeSymbol) {
                        symbol.resetOnStartSpin();
                    }
                } else {
                    symbol.resetOnStartSpin();
                }
            });
            this.xNudgeSymbol = undefined;
        }

        this.scatterSymbol = undefined;
    }
}

export default DDDReel;
